import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, XCircle, ShoppingBag, AlertTriangle,
  Users, CalendarDays, Plus, ChevronRight, ListChecks
} from "lucide-react";
import { getDashboardStats, getTodayAppointments, getAppointments, revokeOutcome } from "@/utils/api";
import OutcomeModal from "@/components/OutcomeModal";
import AppointmentScheduler from "@/components/AppointmentScheduler";
import { toast } from "sonner";

const OUTCOME_ICON = {
  visited: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  not_found: <XCircle className="w-4 h-4 text-amber-500" />,
  order: <ShoppingBag className="w-4 h-4 text-violet-500" />,
};

const OUTCOME_LABEL = {
  visited: "Fatto Visita",
  not_found: "Non Trovato",
  order: "Fatto Ordine",
};

const DASH_CACHE_KEY = "ve_dashboard_cache";

// Restituisce la cache solo se è di oggi, altrimenti null
function loadCacheIfToday() {
  try {
    const raw = JSON.parse(localStorage.getItem(DASH_CACHE_KEY));
    if (!raw) return null;
    const today = new Date().toLocaleDateString("it-IT");
    if (raw.savedAt !== today) {
      localStorage.removeItem(DASH_CACHE_KEY); // cache scaduta → cancella
      return null;
    }
    return raw;
  } catch { return null; }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(() => loadCacheIfToday()?.stats || null);
  const [todayApts, setTodayApts] = useState(() => loadCacheIfToday()?.todayApts || []);
  const [monthApts, setMonthApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calDate, setCalDate] = useState(new Date());
  const [outcomeApt, setOutcomeApt] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);

  const todayStr = format(new Date(), "EEEE d MMMM yyyy", { locale: it });
  const todayISO = format(new Date(), "yyyy-MM-dd");

  const loadData = useCallback(async () => {
    try {
      const [statsRes, todayRes] = await Promise.all([
        getDashboardStats(),
        getTodayAppointments(),
      ]);
      setStats(statsRes.data);
      setTodayApts(todayRes.data);
      // Salva in localStorage per fallback offline
      try {
        localStorage.setItem(DASH_CACHE_KEY, JSON.stringify({
          stats: statsRes.data,
          todayApts: todayRes.data,
          savedAt: new Date().toLocaleDateString("it-IT"),
        }));
      } catch {}
    } catch {
      // Offline o errore: mostra solo i dati cached di oggi (se presenti)
      toast.error("Connessione al server non riuscita. Verifica la connessione o effettua il logout e rientra.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMonthApts = useCallback(async () => {
    const y = calDate.getFullYear();
    const m = String(calDate.getMonth() + 1).padStart(2, "0");
    const start = `${y}-${m}-01`;
    const lastDay = new Date(y, calDate.getMonth() + 1, 0).getDate();
    const end = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
    try {
      const res = await getAppointments({ start_date: start, end_date: end });
      setMonthApts(res.data);
    } catch {}
  }, [calDate]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadMonthApts(); }, [loadMonthApts]);

  const appointmentDates = monthApts.map((a) => {
    try { return parseISO(a.scheduled_date); } catch { return null; }
  }).filter(Boolean);

  const handleRevoke = async (aptId) => {
    try {
      await revokeOutcome(aptId);
      toast.success("Esito annullato");
      loadData();
    } catch {
      toast.error("Errore nell'annullamento dell'esito");
    }
  };

  const pendingToday = todayApts.filter((a) => a.status === "pending");
  const completedToday = todayApts.filter((a) => a.status === "completed");

  return (
    <div className="p-4 md:p-6 space-y-5 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A] capitalize">{todayStr}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Riepilogo giornaliero</p>
        </div>
        <button
          data-testid="new-appointment-btn"
          onClick={() => setShowScheduler(true)}
          className="flex items-center gap-1.5 bg-[#0F172A] text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors active:scale-95"
        >
          <Plus className="w-4 h-4" />
          Nuovo
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          loading={loading}
          label="Oggi"
          value={stats?.today?.total ?? 0}
          sub={`${stats?.today?.completed ?? 0} completati`}
          icon={<CalendarDays className="w-5 h-5 text-blue-500" />}
          color="bg-blue-50"
          testid="stat-today"
        />
        <StatCard
          loading={loading}
          label="In Sospeso"
          value={stats?.today?.pending ?? 0}
          sub="da visitare"
          icon={<ListChecks className="w-5 h-5 text-slate-500" />}
          color="bg-slate-50"
          testid="stat-pending"
        />
        <StatCard
          loading={loading}
          label="Scaduti"
          value={stats?.overdue ?? 0}
          sub="da riprogrammare"
          icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
          color="bg-red-50"
          testid="stat-overdue"
          urgent={stats?.overdue > 0}
        />
        <StatCard
          loading={loading}
          label="Clienti Attivi"
          value={stats?.total_clients ?? 0}
          sub={`Settimana: ${stats?.this_week?.completed ?? 0}/${stats?.this_week?.total ?? 0}`}
          icon={<Users className="w-5 h-5 text-emerald-500" />}
          color="bg-emerald-50"
          testid="stat-clients"
        />
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-5 gap-5">
        {/* Today's appointments */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Appuntamenti Oggi</h2>
            <button
              onClick={() => navigate("/app/oggi")}
              className="text-xs text-blue-600 font-medium flex items-center gap-0.5 hover:underline"
              data-testid="view-all-today"
            >
              Vedi tutti <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
            </div>
          ) : pendingToday.length === 0 && completedToday.length === 0 ? (
            <EmptyState
              icon={<CalendarDays className="w-8 h-8 text-slate-300" />}
              text="Nessun appuntamento oggi"
              sub="Aggiungi un nuovo appuntamento"
            />
          ) : (
            <div className="divide-y divide-slate-50">
              {pendingToday.map((apt) => (
                <AptRow
                  key={apt.id}
                  apt={apt}
                  onOutcome={() => setOutcomeApt(apt)}
                />
              ))}
              {completedToday.map((apt) => (
                <AptRow key={apt.id} apt={apt} completed onRevoke={() => handleRevoke(apt.id)} />
              ))}
            </div>
          )}
        </div>

        {/* Mini Calendar */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-3">Calendario</h2>
          <Calendar
            mode="single"
            selected={new Date(todayISO)}
            month={calDate}
            onMonthChange={setCalDate}
            onSelect={(d) => {
              if (d) navigate(`/app/calendario?date=${format(d, "yyyy-MM-dd")}`);
            }}
            modifiers={{ hasAppointment: appointmentDates }}
            modifiersClassNames={{ hasAppointment: "font-bold underline decoration-blue-400" }}
            locale={it}
            weekStartsOn={1}
            className="w-full"
          />
          <div className="mt-3 pt-3 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
              Appuntamenti
            </span>
          </div>
        </div>
      </div>

      {/* Overdue section */}
      {stats?.overdue > 0 && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors"
          onClick={() => navigate("/app/oggi?filter=overdue")}
          data-testid="overdue-banner"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <div className="font-semibold text-red-800 text-sm">
                {stats.overdue} appuntament{stats.overdue === 1 ? "o" : "i"} scadut{stats.overdue === 1 ? "o" : "i"}
              </div>
              <div className="text-xs text-red-600">Visite programmate non completate</div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-red-400" />
        </div>
      )}

      <OutcomeModal
        appointment={outcomeApt}
        open={!!outcomeApt}
        onClose={() => setOutcomeApt(null)}
        onSuccess={loadData}
      />
      <AppointmentScheduler
        open={showScheduler}
        onClose={() => setShowScheduler(false)}
        onSuccess={loadData}
      />
    </div>
  );
}

function StatCard({ loading, label, value, sub, icon, color, testid, urgent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${urgent ? "border-red-200" : "border-slate-200"} bg-white p-4 shadow-sm`}
    >
      {loading ? (
        <Skeleton className="h-12 w-full rounded-lg" />
      ) : (
        <div className="flex flex-col gap-1">
          <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>{icon}</div>
          <div className={`text-2xl font-heading font-bold ${urgent ? "text-red-600" : "text-[#0F172A]"}`}>{value}</div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="text-[10px] text-slate-400">{sub}</div>
        </div>
      )}
    </div>
  );
}

function AptRow({ apt, onOutcome, completed, onRevoke }) {
  const navigate = useNavigate();
  const clientId = apt.client?.id;
  const clientName = apt.client?.company || apt.client?.name || apt.title || "Appuntamento";

  return (
    <div
      data-testid={`apt-row-${apt.id}`}
      className={`flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors stagger-item slide-up`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${completed ? "bg-slate-300" : "bg-blue-500"}`} />
        <div className="min-w-0">
          {clientId ? (
            <button
              data-testid={`client-name-link-${apt.id}`}
              onClick={() => navigate(`/app/clienti/${clientId}`)}
              className="font-semibold text-sm text-[#0F172A] truncate text-left hover:text-blue-600 hover:underline transition-colors"
            >
              {clientName}
            </button>
          ) : (
            <div className="font-semibold text-sm text-[#0F172A] truncate">{clientName}</div>
          )}
          {apt.client?.last_order_date && (
            <div className="flex items-center gap-0.5 text-[10px] text-violet-500 font-medium mt-0.5">
              <ShoppingBag className="w-2.5 h-2.5 flex-shrink-0" />
              Ord. {new Date(apt.client.last_order_date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "2-digit" })}
            </div>
          )}
          <div className="text-xs text-slate-400 truncate">
            {apt.client?.company ? apt.client?.zone || "—" : apt.client?.zone || "—"}
          </div>
        </div>
      </div>
      {completed ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1 text-xs text-slate-400">
            {OUTCOME_ICON[apt.outcome]}
            <span>{OUTCOME_LABEL[apt.outcome]}</span>
          </div>
          {onRevoke && (
            <button
              data-testid={`revoke-btn-${apt.id}`}
              onClick={onRevoke}
              title="Annulla esito"
              className="text-xs px-2 py-1 rounded-md font-medium border border-slate-200 text-slate-500 hover:text-red-500 hover:border-red-200 transition-all active:scale-95 whitespace-nowrap"
            >
              Revoca
            </button>
          )}
        </div>
      ) : (
        <button
          data-testid={`outcome-btn-${apt.id}`}
          onClick={onOutcome}
          className="flex-shrink-0 bg-[#0F172A] text-white text-xs px-3 py-1.5 rounded-lg font-semibold hover:bg-slate-800 transition-all active:scale-95"
        >
          Esito
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-5 text-center">
      <div className="mb-2">{icon}</div>
      <p className="text-sm font-medium text-slate-600">{text}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}
